import React, { useEffect, useState } from 'react';
import { Mail, Trash2, Eye } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';

const ContactMessagesAdminPage: React.FC = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getContactMessages();
      if (res.success && res.data) setMessages(res.data);
    } catch {
      toast.error('Failed to fetch messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (message: any) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      try {
        await adminAPI.markMessageRead(message.id);
        fetchMessages();
      } catch {}
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await adminAPI.deleteContactMessage(id);
      toast.success('Message deleted');
      setSelectedMessage(null);
      fetchMessages();
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">
          Management
        </p>
        <h1 className="font-serif text-3xl text-primary">
          Contact Messages
          {unreadCount > 0 && (
            <span className="text-nude font-sans text-sm ml-3">
              ({unreadCount} unread)
            </span>
          )}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-1 bg-white border border-light overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-16 rounded" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center">
              <Mail size={32} className="text-nude mx-auto mb-3" />
              <p className="text-xs tracking-widest uppercase text-nude">
                No messages yet
              </p>
            </div>
          ) : (
            <div className="divide-y divide-light max-h-[70vh] overflow-y-auto">
              {messages.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleView(msg)}
                  className={`w-full text-left p-4 hover:bg-light/50 transition-colors ${
                    selectedMessage?.id === msg.id ? 'bg-light' : ''
                  } ${!msg.is_read ? 'border-l-2 border-primary' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-xs ${!msg.is_read ? 'font-semibold text-primary' : 'text-secondary'}`}>
                      {msg.name}
                    </p>
                    {!msg.is_read && (
                      <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-nude truncate">{msg.subject}</p>
                  {msg.whatsapp_number && (
                    <p className="text-xs text-amber-700 mt-0.5">WhatsApp: {msg.whatsapp_number}</p>
                  )}
                  <p className="text-xs text-nude mt-1">
                    {new Date(msg.created_at).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 bg-white border border-light p-8">
          {selectedMessage ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-light pb-6">
                <div>
                  <h2 className="font-serif text-2xl text-primary mb-1">
                    {selectedMessage.subject}
                  </h2>
                  <p className="text-xs text-nude">
                    From {selectedMessage.name} ({selectedMessage.email})
                  </p>
                  <p className="text-xs text-nude mt-1">
                    {new Date(selectedMessage.created_at).toLocaleString()}
                  </p>
                  {selectedMessage.whatsapp_number && (
                    <a
                      href={`https://wa.me/${selectedMessage.whatsapp_number.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 text-xs tracking-widest uppercase bg-amber-50 text-amber-700 px-2 py-1 hover:bg-amber-100 transition-colors"
                    >
                      WhatsApp: {selectedMessage.whatsapp_number}
                    </a>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="text-nude hover:text-red-500 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <p className="text-sm text-secondary leading-relaxed whitespace-pre-wrap">
                {selectedMessage.message}
              </p>
              <a
                href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                className="btn-primary inline-block"
              >
                Reply via Email
              </a>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-24">
              <Eye size={32} className="text-nude mb-3" />
              <p className="text-xs tracking-widest uppercase text-nude">
                Select a message to view
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactMessagesAdminPage;